import { useState, useEffect, useRef, useMemo } from "react";
import { Group, Text, Rect } from "react-konva";

/**
 * PedagogicalTextElement - Text element with colored syllables and silent letters
 * Renders text with reading aids directly in the Konva canvas
 * Based on LireCouleur syllabation rules for French
 */

// Syllable colors (alternating)
const DEFAULT_SYLLABLE_COLORS = ["#1D4ED8", "#C2410C"]; // Blue, Orange
const SILENT_LETTER_COLOR = "#94A3B8";
const SILENT_LETTER_OPACITY = 0.4;

// Line alternation colors
const LINE_COLORS = ["#EFF6FF", "#FFF7ED"]; // Light blue, Light orange

// French phonology constants
const VOWELS = "aeiouyàâäéèêëïîôùûüœæAEIOUYÀÂÄÉÈÊËÏÎÔÙÛÜŒÆ";
const CONSONANTS = "bcdfghjklmnpqrstvwxzçBCDFGHJKLMNPQRSTVWXZÇ";

// Consonant clusters that stay together at the start of a syllable (attaque)
const ONSET_CLUSTERS = [
  "bl", "br", "ch", "cl", "cr", "dr", "fl", "fr", "gl", "gn", "gr",
  "ph", "pl", "pr", "qu", "sc", "sk", "sl", "sm", "sn", "sp", "st",
  "sw", "th", "tr", "vr", "wr",
  // Triple clusters
  "scr", "spl", "spr", "str"
];

// Digraphs and trigraphs (treated as single sounds)
const DIGRAPHS = ["ch", "ph", "th", "gn", "qu", "gu"];

/**
 * French syllable splitter - LireCouleur style
 * Rules:
 * 1. Each syllable must contain at least one vowel
 * 2. Consonant clusters that can start a word stay together
 * 3. Between two vowels, consonants are divided to maximize onset of next syllable
 * 4. "e" at end of word is often silent but kept in syllable
 */
function splitIntoSyllables(word) {
  if (!word || word.length === 0) return [word];
  if (word.length <= 2) return [word];
  
  // Handle special cases
  const lowerWord = word.toLowerCase();
  
  // Check if word has any vowels
  let hasVowel = false;
  for (let i = 0; i < word.length; i++) {
    if (VOWELS.includes(word[i])) {
      hasVowel = true;
      break;
    }
  }
  if (!hasVowel) return [word];
  
  const syllables = [];
  let currentSyllable = "";
  let i = 0;
  
  while (i < word.length) {
    const char = word[i];
    const isVowel = VOWELS.includes(char);
    
    if (isVowel) {
      currentSyllable += char;
      
      // Look ahead to find consonants between this vowel and next
      let consonantBuffer = "";
      let j = i + 1;
      
      while (j < word.length && !VOWELS.includes(word[j])) {
        consonantBuffer += word[j];
        j++;
      }
      
      // If there's a next vowel, decide how to split consonants
      if (j < word.length && consonantBuffer.length > 0) {
        const splitPoint = findSplitPoint(consonantBuffer);
        
        // Add consonants to current syllable (coda)
        if (splitPoint > 0) {
          currentSyllable += consonantBuffer.slice(0, splitPoint);
        }
        
        // Push current syllable and start new one
        syllables.push(currentSyllable);
        currentSyllable = consonantBuffer.slice(splitPoint);
        i = j - 1; // Will be incremented at end of loop
      } else if (consonantBuffer.length > 0 && j >= word.length) {
        // End of word - keep consonants with current syllable
        currentSyllable += consonantBuffer;
        i = j - 1;
      }
    } else {
      // Consonant at start or after another consonant
      currentSyllable += char;
    }
    
    i++;
  }
  
  // Add remaining syllable
  if (currentSyllable.length > 0) {
    syllables.push(currentSyllable);
  }
  
  // Post-processing: merge very short syllables
  const result = [];
  for (let s = 0; s < syllables.length; s++) {
    const syl = syllables[s];
    
    // If syllable is just one consonant without vowel, merge with neighbor
    let hasVowelInSyl = false;
    for (let c = 0; c < syl.length; c++) {
      if (VOWELS.includes(syl[c])) {
        hasVowelInSyl = true;
        break;
      }
    }
    
    if (!hasVowelInSyl && result.length > 0) {
      result[result.length - 1] += syl;
    } else if (!hasVowelInSyl && s < syllables.length - 1) {
      syllables[s + 1] = syl + syllables[s + 1];
    } else {
      result.push(syl);
    }
  }
  
  return result.length > 0 ? result : [word];
}

/**
 * Find the best split point in a consonant cluster
 * Maximizes the onset of the next syllable while respecting French phonotactics
 */
function findSplitPoint(consonants) {
  if (consonants.length === 0) return 0;
  if (consonants.length === 1) return 0; // Single consonant goes to next syllable
  
  const lower = consonants.toLowerCase();
  
  // Check for valid onset clusters from longest to shortest
  for (let len = Math.min(3, lower.length); len >= 2; len--) {
    const potentialOnset = lower.slice(-len);
    if (ONSET_CLUSTERS.includes(potentialOnset)) {
      return consonants.length - len;
    }
  }
  
  // Check for digraphs
  if (lower.length >= 2) {
    const lastTwo = lower.slice(-2);
    if (DIGRAPHS.includes(lastTwo)) {
      return consonants.length - 2;
    }
  }
  
  // Default: split before last consonant (VCCV -> VC.CV)
  // But handle double consonants (mm, nn, etc.) - split between them
  if (lower.length >= 2 && lower[lower.length - 1] === lower[lower.length - 2]) {
    return consonants.length - 1;
  }
  
  // Default: one consonant goes to next syllable
  return consonants.length - 1;
}

/**
 * Detect silent letters in French words
 * Based on common French orthographic patterns
 */
function detectSilentLetters(word) {
  if (!word || word.length < 2) return [];
  
  const silentIndices = [];
  const lowerWord = word.toLowerCase();
  const len = word.length;
  
  // === Final silent letters ===
  
  // Final 'e' muet (most common)
  if (lowerWord.endsWith('e') && len > 2) {
    // Exceptions: words ending in -ée, -ie, etc. where e is pronounced
    const beforeE = lowerWord[len - 2];
    if (!VOWELS.includes(beforeE)) {
      silentIndices.push(len - 1);
    }
  }
  
  // Final 's' (plural marker, usually silent)
  if (lowerWord.endsWith('s') && len > 2) {
    // Exception: words like "bus", "as" - but for pedagogical purposes, mark it
    silentIndices.push(len - 1);
  }
  
  // Final 'x' (plural marker)
  if (lowerWord.endsWith('x') && len > 2) {
    silentIndices.push(len - 1);
  }
  
  // Final 't' (common silent ending)
  if (lowerWord.endsWith('t') && len > 2) {
    silentIndices.push(len - 1);
  }
  
  // Final 'd' (common silent ending)
  if (lowerWord.endsWith('d') && len > 2) {
    silentIndices.push(len - 1);
  }
  
  // Final 'p' (common in "coup", "beaucoup", etc.)
  if (lowerWord.endsWith('p') && len > 2) {
    silentIndices.push(len - 1);
  }
  
  // Final 'g' (in words like "long")
  if (lowerWord.endsWith('g') && len > 2) {
    // But not in -ing
    if (!lowerWord.endsWith('ing')) {
      silentIndices.push(len - 1);
    }
  }
  
  // Final 'c' after 'n' (blanc, franc)
  if (lowerWord.endsWith('nc') && len > 2) {
    silentIndices.push(len - 1);
  }
  
  // === Initial 'h' (always silent in French) ===
  if (lowerWord.startsWith('h')) {
    silentIndices.push(0);
  }
  
  // === Internal 'h' (silent in many contexts) ===
  for (let i = 1; i < len - 1; i++) {
    if (lowerWord[i] === 'h') {
      // 'h' between vowels or after consonant is usually silent
      // Exception: in borrowed words it might be pronounced
      silentIndices.push(i);
    }
  }
  
  // === Double letters (second one often represents length, not separate sound) ===
  // Mark doubled consonants for visual differentiation (optional - can be toggled)
  
  // === 'ent' ending for verbs (3rd person plural) ===
  if (lowerWord.endsWith('ent') && len > 4) {
    // The whole 'ent' is silent in verb conjugations
    // But for simplicity, just mark 'ent'
    silentIndices.push(len - 3); // e
    silentIndices.push(len - 2); // n  
    silentIndices.push(len - 1); // t
  }
  
  // Remove duplicates and sort
  return [...new Set(silentIndices)].sort((a, b) => a - b);
}

/**
 * Get grapheme-phoneme highlighting (arcs sous les sons complexes)
 */
function getComplexGraphemes(word) {
  const graphemes = [];
  const lowerWord = word.toLowerCase();
  
  const complexPatterns = [
    { pattern: 'eau', color: '#059669' },  // o sound
    { pattern: 'au', color: '#059669' },   // o sound
    { pattern: 'ou', color: '#7C3AED' },   // ou sound
    { pattern: 'oi', color: '#DC2626' },   // wa sound
    { pattern: 'ai', color: '#EA580C' },   // è sound
    { pattern: 'ei', color: '#EA580C' },   // è sound
    { pattern: 'an', color: '#0891B2' },   // nasal
    { pattern: 'en', color: '#0891B2' },   // nasal
    { pattern: 'am', color: '#0891B2' },   // nasal
    { pattern: 'em', color: '#0891B2' },   // nasal
    { pattern: 'on', color: '#BE185D' },   // nasal
    { pattern: 'om', color: '#BE185D' },   // nasal
    { pattern: 'in', color: '#4F46E5' },   // nasal
    { pattern: 'im', color: '#4F46E5' },   // nasal
    { pattern: 'un', color: '#4F46E5' },   // nasal
    { pattern: 'ain', color: '#4F46E5' },  // nasal
    { pattern: 'ein', color: '#4F46E5' },  // nasal
    { pattern: 'ch', color: '#F59E0B' },   // ch sound
    { pattern: 'ph', color: '#F59E0B' },   // f sound
    { pattern: 'gn', color: '#10B981' },   // ñ sound
    { pattern: 'qu', color: '#6366F1' },   // k sound
    { pattern: 'gu', color: '#6366F1' },   // g sound (before e, i)
  ];
  
  for (const { pattern, color } of complexPatterns) {
    let index = lowerWord.indexOf(pattern);
    while (index !== -1) {
      graphemes.push({
        start: index,
        end: index + pattern.length,
        color,
        pattern
      });
      index = lowerWord.indexOf(pattern, index + 1);
    }
  }
  
  return graphemes;
}

/**
 * PedagogicalTextKonva - Konva component for pedagogical text
 */
export function PedagogicalTextKonva({
  element,
  isSelected,
  onSelect,
  onDragEnd,
  onTransform,
}) {
  const groupRef = useRef();
  
  const {
    text = "",
    fontSize = 20,
    fontFamily = "Nunito",
    fill = "#1e293b",
    width = 280,
    x = 0,
    y = 0,
    showColoredSyllables = true,
    showSilentLetters = true,
    showLineAlternation = false,
    syllableColors = DEFAULT_SYLLABLE_COLORS,
    lineHeight = 1.6,
    letterSpacing = 0,
    wordSpacing = 0,
  } = element;

  // Parse text into words and render segments
  const renderSegments = useMemo(() => {
    if (!text) return [];
    
    const words = text.split(/(\s+)/);
    const segments = [];
    let currentX = 0;
    let currentY = 0;
    let syllableIndex = 0;
    const lineHeightPx = fontSize * lineHeight;
    const spaceWidth = fontSize * 0.3;
    
    words.forEach((word, wordIndex) => {
      // Handle whitespace
      if (/^\s+$/.test(word)) {
        currentX += spaceWidth + wordSpacing;
        return;
      }
      
      // Skip empty words
      if (!word) return;
      
      // Detect silent letters
      const silentIndices = showSilentLetters ? detectSilentLetters(word) : [];
      
      if (showColoredSyllables) {
        // Split into syllables
        const syllables = splitIntoSyllables(word);
        let charIndex = 0;
        
        syllables.forEach((syllable, sylIndex) => {
          const color = syllableColors[syllableIndex % syllableColors.length];
          syllableIndex++;
          
          // Render each character in the syllable
          for (let i = 0; i < syllable.length; i++) {
            const char = syllable[i];
            const globalCharIndex = charIndex + i;
            const isSilent = silentIndices.includes(globalCharIndex);
            
            segments.push({
              type: "char",
              text: char,
              x: currentX,
              y: currentY,
              fill: isSilent ? SILENT_LETTER_COLOR : color,
              opacity: isSilent ? SILENT_LETTER_OPACITY : 1,
              fontSize,
              fontFamily,
            });
            
            // Approximate character width
            currentX += fontSize * 0.6 + letterSpacing;
          }
          
          charIndex += syllable.length;
        });
      } else {
        // Render word without syllable coloring
        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const isSilent = silentIndices.includes(i);
          
          segments.push({
            type: "char",
            text: char,
            x: currentX,
            y: currentY,
            fill: isSilent ? SILENT_LETTER_COLOR : fill,
            opacity: isSilent ? SILENT_LETTER_OPACITY : 1,
            fontSize,
            fontFamily,
          });
          
          currentX += fontSize * 0.6 + letterSpacing;
        }
      }
      
      // Add space after word
      currentX += spaceWidth + wordSpacing;
      
      // Check for line wrap
      if (currentX > width - fontSize) {
        currentX = 0;
        currentY += lineHeightPx;
      }
    });
    
    return segments;
  }, [text, fontSize, fontFamily, fill, width, showColoredSyllables, showSilentLetters, syllableColors, lineHeight, letterSpacing, wordSpacing]);

  // Calculate total height
  const totalHeight = useMemo(() => {
    if (renderSegments.length === 0) return fontSize * lineHeight;
    const maxY = Math.max(...renderSegments.map(s => s.y));
    return maxY + fontSize * lineHeight;
  }, [renderSegments, fontSize, lineHeight]);

  const handleDragEnd = (e) => {
    onDragEnd?.({
      ...element,
      x: e.target.x(),
      y: e.target.y(),
    });
  };

  return (
    <Group
      ref={groupRef}
      x={x}
      y={y}
      draggable={!element.locked}
      onClick={onSelect}
      onTap={onSelect}
      onDragEnd={handleDragEnd}
    >
      {/* Background for line alternation */}
      {showLineAlternation && (
        <>
          {Array.from({ length: Math.ceil(totalHeight / (fontSize * lineHeight)) }).map((_, lineIndex) => (
            <Rect
              key={`line-bg-${lineIndex}`}
              x={-4}
              y={lineIndex * fontSize * lineHeight - 2}
              width={width + 8}
              height={fontSize * lineHeight}
              fill={LINE_COLORS[lineIndex % LINE_COLORS.length]}
              cornerRadius={4}
            />
          ))}
        </>
      )}

      {/* Render text segments */}
      {renderSegments.map((segment, index) => (
        <Text
          key={index}
          x={segment.x}
          y={segment.y}
          text={segment.text}
          fontSize={segment.fontSize}
          fontFamily={segment.fontFamily}
          fill={segment.fill}
          opacity={segment.opacity}
        />
      ))}

      {/* Selection border */}
      {isSelected && (
        <Rect
          x={-4}
          y={-4}
          width={width + 8}
          height={totalHeight + 8}
          stroke="#6366f1"
          strokeWidth={2}
          dash={[4, 4]}
          fill="transparent"
        />
      )}
    </Group>
  );
}

/**
 * PedagogicalTextPreview - HTML preview component (for properties panel)
 */
export function PedagogicalTextPreview({ 
  text, 
  showColoredSyllables = true,
  showSilentLetters = true,
  syllableColors = DEFAULT_SYLLABLE_COLORS,
}) {
  if (!text) return null;
  
  const words = text.split(/(\s+)/);
  let syllableIndex = 0;
  
  return (
    <div className="text-base leading-relaxed">
      {words.map((word, wordIndex) => {
        if (/^\s+$/.test(word)) {
          return <span key={wordIndex}> </span>;
        }
        
        const silentIndices = showSilentLetters ? detectSilentLetters(word) : [];
        
        if (showColoredSyllables) {
          const syllables = splitIntoSyllables(word);
          
          return (
            <span key={wordIndex}>
              {syllables.map((syllable, sylIndex) => {
                const color = syllableColors[syllableIndex % syllableColors.length];
                syllableIndex++;
                
                return (
                  <span key={sylIndex}>
                    {syllable.split("").map((char, charIndex) => {
                      const globalIndex = syllable.slice(0, charIndex).length;
                      // Find if this char position is silent in original word
                      let wordCharIndex = 0;
                      for (let s = 0; s < sylIndex; s++) {
                        wordCharIndex += syllables[s].length;
                      }
                      wordCharIndex += charIndex;
                      
                      const isSilent = silentIndices.includes(wordCharIndex);
                      
                      return (
                        <span
                          key={charIndex}
                          style={{
                            color: isSilent ? SILENT_LETTER_COLOR : color,
                            opacity: isSilent ? SILENT_LETTER_OPACITY : 1,
                          }}
                        >
                          {char}
                        </span>
                      );
                    })}
                  </span>
                );
              })}
            </span>
          );
        }
        
        return (
          <span key={wordIndex}>
            {word.split("").map((char, charIndex) => {
              const isSilent = silentIndices.includes(charIndex);
              return (
                <span
                  key={charIndex}
                  style={{
                    color: isSilent ? SILENT_LETTER_COLOR : undefined,
                    opacity: isSilent ? SILENT_LETTER_OPACITY : 1,
                  }}
                >
                  {char}
                </span>
              );
            })}
          </span>
        );
      })}
    </div>
  );
}

export default {
  PedagogicalTextKonva,
  PedagogicalTextPreview,
  splitIntoSyllables,
  detectSilentLetters,
  DEFAULT_SYLLABLE_COLORS,
};
